/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UserRole } from 'src/graphql/generated/graphql.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionnaireSectionQuestionProps {
  id: string;
  questionnaireSectionId: string;
  questionId: string;
  explanationRequiredChoices: List<IdListItemProps> | null;
  reviewRequiredChoices: List<IdListItemProps> | null;
  responsibleRole: UserRole;
  sequence: number;
  activationRuleId: string | null;
  isDisabled: boolean;
  activityType: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionnaireSectionQuestionRecord extends Record<QuestionnaireSectionQuestionProps>({
  id: null as any,
  questionnaireSectionId: null as any,
  questionId: null as any,
  explanationRequiredChoices: null as any,
  reviewRequiredChoices: null as any,
  responsibleRole: null as any,
  sequence: null as any,
  activationRuleId: null as any,
  isDisabled: null as any,
  activityType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    questionnaireSectionId: string;
    questionId: string;
    explanationRequiredChoices: any;
    reviewRequiredChoices: any;
    responsibleRole: UserRole;
    sequence: number;
    activationRuleId: string | null;
    isDisabled: boolean;
    activityType: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionnaireSectionQuestionRecord {
    if (json == null) {
      throw new Error('QuestionnaireSectionQuestionRecord.fromJS: parameter cannot be null');
    }
    return new QuestionnaireSectionQuestionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireSectionQuestionJson.id',
      ),
      questionnaireSectionId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'questionnaireSectionId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireSectionQuestionJson.questionnaireSectionId',
      ),
      questionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'questionId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireSectionQuestionJson.questionId',
      ),
      explanationRequiredChoices:
        json.explanationRequiredChoices != null
          ? List<IdListItemRecord>(
              json.explanationRequiredChoices.map((o: any) => new IdListItemRecord(o)),
            )
          : null,
      reviewRequiredChoices:
        json.reviewRequiredChoices != null
          ? List<IdListItemRecord>(
              json.reviewRequiredChoices.map((o: any) => new IdListItemRecord(o)),
            )
          : null,
      responsibleRole: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'responsibleRole', UserRole.Developer),
        'QuestionnaireSectionQuestionJson.responsibleRole',
      ),
      sequence: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'sequence', 0),
        'QuestionnaireSectionQuestionJson.sequence',
      ),
      activationRuleId: json.activationRuleId,
      isDisabled: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isDisabled', false),
        'QuestionnaireSectionQuestionJson.isDisabled',
      ),
      activityType: json.activityType,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    questionnaireSectionId: string;
    questionId: string;
    explanationRequiredChoices: any;
    reviewRequiredChoices: any;
    responsibleRole: UserRole;
    sequence: number;
    activationRuleId: string | null;
    isDisabled: boolean;
    activityType: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionnaireSectionQuestionRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionnaireSectionQuestionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionnaireSectionQuestionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireSectionQuestionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireSectionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'questionnaireSectionId',
                '00000000-0000-0000-0000-000000000000',
              ),
            'QuestionnaireSectionQuestionJson.questionnaireSectionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'questionId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireSectionQuestionJson.questionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      explanationRequiredChoices: tryOrDefault(
        () =>
          json.explanationRequiredChoices != null
            ? List<IdListItemRecord>(
                json.explanationRequiredChoices.map((o: any) => new IdListItemRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reviewRequiredChoices: tryOrDefault(
        () =>
          json.reviewRequiredChoices != null
            ? List<IdListItemRecord>(
                json.reviewRequiredChoices.map((o: any) => new IdListItemRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      responsibleRole: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'responsibleRole', UserRole.Developer),
            'QuestionnaireSectionQuestionJson.responsibleRole',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sequence: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'sequence', 0),
            'QuestionnaireSectionQuestionJson.sequence',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activationRuleId: tryOrDefault(
        () => json.activationRuleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isDisabled: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isDisabled', false),
            'QuestionnaireSectionQuestionJson.isDisabled',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activityType: tryOrDefault(
        () => json.activityType,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionnaireSectionQuestionProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionnaireSectionQuestionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionnaireSectionQuestionViewModelBase
  implements QuestionnaireSectionQuestionProps
{
  protected readonly _props: QuestionnaireSectionQuestionProps;
  private _json?: QuestionnaireSectionQuestionProps;

  get id(): string {
    return this._props.id;
  }
  get questionnaireSectionId(): string {
    return this._props.questionnaireSectionId;
  }
  get questionId(): string {
    return this._props.questionId;
  }
  get explanationRequiredChoices(): List<IdListItemProps> | null {
    return this._props.explanationRequiredChoices;
  }
  get reviewRequiredChoices(): List<IdListItemProps> | null {
    return this._props.reviewRequiredChoices;
  }
  get responsibleRole(): UserRole {
    return this._props.responsibleRole;
  }
  get sequence(): number {
    return this._props.sequence;
  }
  get activationRuleId(): string | null {
    return this._props.activationRuleId;
  }
  get isDisabled(): boolean {
    return this._props.isDisabled;
  }
  get activityType(): string | null {
    return this._props.activityType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAsbDataManager() {
    return this._props.responsibleRole === UserRole.AsbDataManager;
  }
  public get isAsbViewer() {
    return this._props.responsibleRole === UserRole.AsbViewer;
  }
  public get isAviationDataManager() {
    return this._props.responsibleRole === UserRole.AviationDataManager;
  }
  public get isAviationPermissionsManager() {
    return this._props.responsibleRole === UserRole.AviationPermissionsManager;
  }
  public get isAviationSupervisor() {
    return this._props.responsibleRole === UserRole.AviationSupervisor;
  }
  public get isBiller() {
    return this._props.responsibleRole === UserRole.Biller;
  }
  public get isBillerCorrectionsTeam() {
    return this._props.responsibleRole === UserRole.BillerCorrectionsTeam;
  }
  public get isClinical() {
    return this._props.responsibleRole === UserRole.Clinical;
  }
  public get isCommunications() {
    return this._props.responsibleRole === UserRole.Communications;
  }
  public get isCommunicationsSupervisor() {
    return this._props.responsibleRole === UserRole.CommunicationsSupervisor;
  }
  public get isComplianceManager() {
    return this._props.responsibleRole === UserRole.ComplianceManager;
  }
  public get isDeveloper() {
    return this._props.responsibleRole === UserRole.Developer;
  }
  public get isExecutive() {
    return this._props.responsibleRole === UserRole.Executive;
  }
  public get isExternal() {
    return this._props.responsibleRole === UserRole.External;
  }
  public get isFixedWingCoordinator() {
    return this._props.responsibleRole === UserRole.FixedWingCoordinator;
  }
  public get isFleetStatusOperations() {
    return this._props.responsibleRole === UserRole.FleetStatusOperations;
  }
  public get isFleetStatusViewer() {
    return this._props.responsibleRole === UserRole.FleetStatusViewer;
  }
  public get isFlightLogViewer() {
    return this._props.responsibleRole === UserRole.FlightLogViewer;
  }
  public get isGlobalDataManager() {
    return this._props.responsibleRole === UserRole.GlobalDataManager;
  }
  public get isGlobalPermissionsManager() {
    return this._props.responsibleRole === UserRole.GlobalPermissionsManager;
  }
  public get isImageTrend() {
    return this._props.responsibleRole === UserRole.ImageTrend;
  }
  public get isMechanic() {
    return this._props.responsibleRole === UserRole.Mechanic;
  }
  public get isOCC() {
    return this._props.responsibleRole === UserRole.OCC;
  }
  public get isOccReader() {
    return this._props.responsibleRole === UserRole.OccReader;
  }
  public get isOosMaintenanceController() {
    return this._props.responsibleRole === UserRole.OosMaintenanceController;
  }
  public get isOosQA() {
    return this._props.responsibleRole === UserRole.OosQA;
  }
  public get isOosViewer() {
    return this._props.responsibleRole === UserRole.OosViewer;
  }
  public get isOperationsDataManager() {
    return this._props.responsibleRole === UserRole.OperationsDataManager;
  }
  public get isOperationsPermissionsManager() {
    return this._props.responsibleRole === UserRole.OperationsPermissionsManager;
  }
  public get isOpsReader() {
    return this._props.responsibleRole === UserRole.OpsReader;
  }
  public get isOrgReader() {
    return this._props.responsibleRole === UserRole.OrgReader;
  }
  public get isPilot() {
    return this._props.responsibleRole === UserRole.Pilot;
  }
  public get isPrdAdmin() {
    return this._props.responsibleRole === UserRole.PrdAdmin;
  }
  public get isPrdUser() {
    return this._props.responsibleRole === UserRole.PrdUser;
  }
  public get isProgramManager() {
    return this._props.responsibleRole === UserRole.ProgramManager;
  }
  public get isQaManager() {
    return this._props.responsibleRole === UserRole.QaManager;
  }
  public get isQaSupervisor() {
    return this._props.responsibleRole === UserRole.QaSupervisor;
  }
  public get isRemoteUser() {
    return this._props.responsibleRole === UserRole.RemoteUser;
  }
  public get isStakeholder() {
    return this._props.responsibleRole === UserRole.Stakeholder;
  }
  public get isSupport() {
    return this._props.responsibleRole === UserRole.Support;
  }
  public get isTrmProgramAdmin() {
    return this._props.responsibleRole === UserRole.TrmProgramAdmin;
  }

  public isAnyResponsibleRole(...userRole: UserRole[]) {
    return userRole.includes(this._props.responsibleRole);
  }

  constructor(props: QuestionnaireSectionQuestionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        questionnaireSectionId: this._props.questionnaireSectionId,
        questionId: this._props.questionId,
        explanationRequiredChoices: this._props.explanationRequiredChoices,
        reviewRequiredChoices: this._props.reviewRequiredChoices,
        responsibleRole: this._props.responsibleRole,
        sequence: this._props.sequence,
        activationRuleId: this._props.activationRuleId,
        isDisabled: this._props.isDisabled,
        activityType: this._props.activityType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionnaireSectionQuestionEntities = (state: AppState) =>
  state.entityStores.questionnaireSectionQuestion.entities;

export const selectQuestionnaireSectionQuestionById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireSectionQuestion.entities.get(id);

export const selectQuestionnaireSectionQuestionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaireSectionQuestion.meta.get(id);

export const selectQuestionnaireSectionQuestionAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionnaireSectionQuestionById(id),
    selectQuestionnaireSectionQuestionMetadataById(id),
    (questionnaireSectionQuestion, meta) => ({ questionnaireSectionQuestion, meta }),
  );

export function selectQuestionnaireSectionQuestionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionnaireSectionQuestionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionnaireSectionQuestionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionnaireSectionQuestionEntitiesByQuestionnaireSectionId(
  questionnaireSectionId: string,
) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.questionnaireSectionId === questionnaireSectionId).valueSeq(),
  );
}

export function selectQuestionnaireSectionQuestionEntitiesByQuestionId(questionId: string) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.questionId === questionId).valueSeq(),
  );
}

export function selectQuestionnaireSectionQuestionEntitiesByResponsibleRole(
  responsibleRole: UserRole,
) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.responsibleRole === responsibleRole).valueSeq(),
  );
}

export function selectQuestionnaireSectionQuestionEntitiesBySequence(sequence: number) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.sequence === sequence).valueSeq(),
  );
}

export function selectQuestionnaireSectionQuestionEntitiesByActivationRuleId(
  activationRuleId: string,
) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.activationRuleId === activationRuleId).valueSeq(),
  );
}

export function selectQuestionnaireSectionQuestionEntitiesByIsDisabled(isDisabled: boolean) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.isDisabled === isDisabled).valueSeq(),
  );
}

export function selectQuestionnaireSectionQuestionEntitiesByActivityType(activityType: string) {
  return createSelector(selectAllQuestionnaireSectionQuestionEntities, (entities) =>
    entities.filter((e) => e.activityType === activityType).valueSeq(),
  );
}
//#endregion Selectors
